//region :: Imports ::
import React, {createRef, ReactElement, RefObject} from 'react';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import MForm from '../../Toolkit/Components/Controls/Form';
import MPage, {MPageProps} from '../../Toolkit/Components/Shell/Page';
import MToolbar from '../../Toolkit/Components/Controls/Toolbar';
import MComponent from '../../Toolkit/Components/Component';
import MSpacer from '../../Toolkit/Components/Controls/Spacer';
import MFormSection from '../../Toolkit/Components/Controls/FormSection';
import {MFormOutcome} from '../../Toolkit/Components/Controls/Form';
import WithRouterProps from '../../Toolkit/WithRouterProps';
import withRouter from '../../Toolkit/WithRouter';
import {FieldProps, FormikHelpers} from 'formik';
import * as Yup from 'yup';
import NSwagAdapter from '../../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../../Toolkit/Api/NSwagClientConfiguration';
import TrafficManagementAccessTokenProvider from '../../Api/TrafficManagementAccessTokenProvider';
import {MNullable, toErrorMessage} from '../../Toolkit/Utils';
import MAlertDialog from '../../Toolkit/Components/Controls/AlertDialog';
import MFormFieldControl from '../../Toolkit/Components/Controls/FormFieldControl';
import MTextBox from '../../Toolkit/Components/Controls/TextBox';
import MDropDown from '../../Toolkit/Components/Controls/DropDown';
import MSaveButton from '../../Toolkit/Components/Controls/SaveButton';
import {MNavigateToIconName, MSuccessIcon} from '../../Toolkit/Icons';
import MButton from '../../Toolkit/Components/Controls/Button';
import {CreateCrewRequest, CrewClient} from '../../Api/CrewClient';
import {CreateCrewValidator, Crew, CrewData, CrewDataReply} from '../../Api/ControllerTypes/CrewTypes';
import MBackButton from '../../Toolkit/Components/Controls/BackButton';
import EnumContext from '../../Context/enum-context';

//endregion

/** */
type RouteParams = {
    id: string;
};

type CreateCrewData = {
    crewName: string;
    crewType: number;
    workArea: string;
}

/** */
enum PageActions {
    None = 0,
    Saving = 1
}

/** */
type Props = WithMsalProps & WithRouterProps<RouteParams> & {}

/** */
type State = {
    pageTitle: string;
    pageSubtitle: string;
    page: MPageProps;
    form: {
        id: string;
        data: CreateCrewData;
        validationSchema: any;
    }
    pageActions: PageActions;
}

/** */
class CreateCrewPage extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private m_newCrew: MNullable<CrewData> | undefined;
    private readonly m_routeParams: RouteParams;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_formRef: RefObject<MForm<FormData>>;
    private readonly m_crewClient: NSwagAdapter<CrewClient>;
    private readonly m_alertDialogRef: RefObject<MAlertDialog>;
    
    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_routeParams = this.props.match.params;
        this.m_pageRef = createRef<MPage>();
        this.m_formRef = createRef<MForm<FormData>>();
        this.m_crewClient = new NSwagAdapter<CrewClient>(new CrewClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_alertDialogRef = createRef<MAlertDialog>();

        this.initializeState();

    }

    /** */
    private bindMethods(): void {

        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.close_Click = this.close_Click.bind(this);
        this.form_onBeforeSubmit = this.form_onBeforeSubmit.bind(this);
        this.form_onSubmit = this.form_onSubmit.bind(this);
        this.form_onAfterSubmit = this.form_onAfterSubmit.bind(this);
        this.form_onError = this.form_onError.bind(this);
        this.navigateToCrewButton_onClick = this.navigateToCrewButton_onClick.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);

    }

    /** */
    private initializeState(): void {
        Yup.addMethod(Yup.string, 'checkDuplicate', function validateName() {
            return this.test('crewname-duplicate', 'Crew name must be unique', function(value) {
                return new Promise((resolve, reject) => {
                    if(value === 'Test') {
                        reject('Crew name must be unique');
                    }
                    resolve(true);
                });
            });
        });
        //  Page
        const page: MPageProps = {locked: false};

        const data: CreateCrewData = {
            crewName: '',
            crewType: 0,
            workArea: ''
        };

        const validationSchema: any = Yup.object().shape({
            crewName: CreateCrewValidator.crewNameFunc(this.m_crewClient, true),
            crewType: CreateCrewValidator.crewTypeFunc(this.m_crewClient, true),
            workArea: CreateCrewValidator.workArea
        });
        
        this.state = {
            pageTitle: '',
            pageSubtitle: '',
            page: page,
            form: {
                id: 'createCrewForm',
                data: data,
                validationSchema: validationSchema,
            },
            pageActions: PageActions.None
        };

    }

    public componentDidMount(): void {
    }

    private form_onBeforeSubmit(fieldValues: CreateCrewData, formikHelpers: FormikHelpers<CreateCrewData>): boolean {
        this.safeSetState(draft => draft.pageActions = PageActions.Saving);
        return false;
    }

    private async form_onSubmit(fieldValues: CreateCrewData, formikHelpers: FormikHelpers<CreateCrewData>): Promise<void> {
        const createRequest = new CreateCrewRequest({
            crewName: fieldValues.crewName,
            crewType: fieldValues.crewType,
            workArea: fieldValues.workArea
        });

        const result = await this.m_crewClient.callWithReply(CrewDataReply, (client => client.create(createRequest)));
        result.throwIfFailed()

        this.m_newCrew = result.reply!.crew;
    }

    private form_onAfterSubmit(outcome: MFormOutcome, fieldValues: CreateCrewData, formikHelpers: FormikHelpers<CreateCrewData>, exception: any | undefined): void {
        if(outcome === 'success') {

            this.m_alertDialogRef.current!.show(
                'Create Crew',
                `A new crew has been created for ${this.m_newCrew!.crew.crewName}.`,
                MSuccessIcon,
                <MButton label="Open new crew"
                         color="success"
                         icon={MNavigateToIconName}
                         disabled={false}
                         onClick={this.navigateToCrewButton_onClick}/>);

            return;

        }

        this.safeSetState((draft: State) => {

            draft.pageActions &= ~PageActions.Saving;
            draft.pageActions = PageActions.None;

        });
    }

    private navigateToCrewButton_onClick(): void {
        this.props.history.replace(`/crews/${this.m_newCrew?.crew.id}`);
    }

    private form_onError(exception: any): boolean
    {
        const handled = false;
        this.m_pageRef.current!.messageBar.error(toErrorMessage(exception));
        return handled;
    }

    private async page_onLoadData(): Promise<void> {
        this.safeSetState((draft) => {
            draft.pageTitle = 'Create Crew';
        });
    }

    /** */
    private getToolbarTemplate(): ReactElement<MToolbar> {

        return (
            <MToolbar>
                <MBackButton disabled={false}/>
                <MSpacer/>
                <MSaveButton formId={this.state.form.id}
                             isBusy={this.isSaving}
                             disabled={this.isFormDisabled}/>
            </MToolbar>
        );

    }

    private async close_Click(): Promise<void> {
        this.props.history.goBack();
    }

    /** */
    public render(): ReactElement {

        return (
            <MPage title={this.state.pageTitle}
                   subtitle={this.state.pageSubtitle}
                   locked={this.state.page.locked}
                   width={12}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <MAlertDialog ref={this.m_alertDialogRef}/>

                <MForm formId={this.state.form.id}
                       initialValues={this.state.form.data}
                       validationSchema={this.state.form.validationSchema}
                       onBeforeSubmit={this.form_onBeforeSubmit}
                       onSubmit={this.form_onSubmit}
                       onAfterSubmit={this.form_onAfterSubmit}
                       onError={this.form_onError}>

                    <div className="formgrid grid">
                        <div className="col-12">

                            <MFormSection title="General">
                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Crew Name"
                                                       fieldName="crewName">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={Crew.CrewNameMaxLength}
                                                          readonly={false}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}/>
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label={'Crew Type'}
                                                       fieldName={'crewType'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MDropDown<number> id="CrewType"
                                                                   disabled={false}
                                                                   fieldProps={fieldProps}
                                                                   items={this.context?.enums === null ? [] : this.context?.enums.crewTypes.filter(p => p.id >= 0)
                                                                                              .map((enumvalue) => {
                                                                                                  return {label: enumvalue.name, value: enumvalue.id};
                                                                                              })}
                                                                   initialValue={-1}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                            </MFormSection>

                        </div>
                    </div>

                </MForm>

            </MPage>

        );

    }

    private get isFormDisabled(): boolean{
        return this.isSaving;
    }

    private get isSaving() {
        return (this.state.pageActions === PageActions.Saving);
    }
}

export default withMsal(withRouter(CreateCrewPage));